import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Lock, ArrowRight, Sparkles, Loader2 } from "lucide-react";
import { NeedAuth } from "@/pages/programs/_gate";
import { RenewalNotice } from "@/components/RenewalNotice";

const PROGRAMS = [
  { key: "shift_society", name: "Shift+ Membership", price: "$33/mo", path: "/programs/shift-plus", accent: true },
  { key: "frequency_reset", name: "7-Day Frequency Reset", price: "$33", path: "/programs/frequency-reset" },
  { key: "timeline_workbook", name: "Timeline Shift Workbook", price: "$55", path: "/programs/timeline-workbook" },
  { key: "thirty_day_shift", name: "The 30-Day Shift", price: "$88", path: "/programs/thirty-day-shift" },
  { key: "reprogram_reality", name: "Reprogram Your Reality", price: "$199", path: "/programs/reprogram-reality" },
];

export default function Access() {
  const { user, loading: authLoading } = useAuth();
  const [access, setAccess] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading || !user) {
      setLoading(false);
      return;
    }
    api
      .get("/me/access")
      .then((r) => setAccess(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user, authLoading]);

  if (!user && !authLoading) return <NeedAuth />;
  if (loading) {
    return (
      <div className="pt-24 min-h-screen flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-accent" />
      </div>
    );
  }

  const unlockedCount = access ? Object.values(access.access).filter(Boolean).length : 0;

  return (
    <div className="pt-20 sm:pt-24" data-testid="access-page">
      <section className="mx-auto max-w-5xl px-5 sm:px-6 py-10 sm:py-16">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <div className="eyebrow mb-4 text-accent flex items-center gap-2">
            <Sparkles className="h-3 w-3" /> Your access
          </div>
          <h1 className="font-serif text-[2.5rem] leading-[1.05] sm:text-6xl mb-4">
            Welcome, <span className="italic text-accent">{user?.name || "seeker"}</span>.
          </h1>
          <p className="text-muted-foreground text-base sm:text-lg mb-10 sm:mb-12 leading-relaxed max-w-2xl">
            {unlockedCount > 0
              ? `You have access to ${unlockedCount} program${unlockedCount === 1 ? "" : "s"}. Return anytime.`
              : "You haven't unlocked any programs yet. Start with the free Shift Reader, or open a paid program below."}
          </p>

          <RenewalNotice />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            {PROGRAMS.map((p) => {
              const unlocked = access?.access?.[p.key];
              return (
                <div
                  key={p.key}
                  className={`border p-6 sm:p-8 flex flex-col justify-between ${
                    unlocked ? "border-accent bg-accent/5" : "border-border"
                  } ${p.accent && !unlocked ? "sm:col-span-2" : ""}`}
                  data-testid={`access-card-${p.key}`}
                >
                  <div>
                    <div className="flex items-center justify-between mb-3 gap-3">
                      <div className="eyebrow">{p.price}</div>
                      {unlocked ? (
                        <span className="font-mono text-[10px] uppercase tracking-widest text-accent flex items-center gap-1">
                          <Sparkles className="h-3 w-3" /> Unlocked
                        </span>
                      ) : (
                        <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1">
                          <Lock className="h-3 w-3" /> Locked
                        </span>
                      )}
                    </div>
                    <h3 className="font-serif text-2xl sm:text-3xl mb-2 leading-snug">{p.name}</h3>
                    {p.key === "shift_society" && unlocked && access?.member_until && (
                      <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground" data-testid="access-member-until">
                        Active until {new Date(access.member_until).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })}
                      </p>
                    )}
                  </div>
                  <div className="mt-6">
                    {unlocked ? (
                      <Link to={p.path}>
                        <Button className="w-full sm:w-auto btn-pill font-mono uppercase text-xs tracking-widest h-12 px-6 gap-2" data-testid={`access-open-${p.key}`}>
                          Open <ArrowRight className="h-4 w-4" />
                        </Button>
                      </Link>
                    ) : (
                      <Link to="/membership">
                        <Button variant="outline" className="w-full sm:w-auto btn-pill font-mono uppercase text-xs tracking-widest h-12 px-6 gap-2" data-testid={`access-unlock-${p.key}`}>
                          Unlock <ArrowRight className="h-4 w-4" />
                        </Button>
                      </Link>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </motion.div>
      </section>
    </div>
  );
}
